import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ButtonColor, ButtonType } from '@klubovna/shared-models';
import { GtagService } from '@klubovna/shared-data';

@Component({
  selector: 'k-button',
  imports: [ NgClass ],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
  private gtagService: GtagService = inject(GtagService);

  type = input<ButtonType>('button');
  icon = input();
  outlined = input(false);
  circle = input(false);
  fullWidth = input(false);
  color = input<ButtonColor>(ButtonColor.Neon);
  gtagLabel = input<string>();
  showIconOnMobile = input(true);
  disabled = input(false);
  isLoading = input(false);
  testId = input<string>();

  class = computed(() => {
    let classes = !this.fullWidth() ? '' : 'w-full h-[56px] text-lg ';

    switch (this.color()) {
      case ButtonColor.Neon:
        classes += this.outlined()
          ? 'text-neon border-neon disabled:hover:bg-transparent disabled:hover:text-neon hover:md:bg-neon hover:md:text-night'
          : 'text-night bg-neon disabled:hover:bg-neon disabled:hover:text-night hover:md:bg-white';
        break;
      case ButtonColor.White:
        classes += this.outlined()
          ? 'text-white border-white disabled:hover:bg-transparent disabled:hover:text-white hover:md:bg-white hover:md:text-night'
          : 'text-night bg-white disabled:hover:bg-white disabled:hover:text-night hover:md:bg-night hover:md:text-white';
        break;
      case ButtonColor.Black:
        classes += this.outlined()
          ? 'text-night border-night disabled:hover:bg-transparent disabled:hover:text-night hover:md:bg-night hover:md:text-white'
          : 'text-neon bg-night disabled:hover:bg-night disabled:hover:text-neon hover:md:bg-white hover:md:text-night';
        break;
      case ButtonColor.Red:
        classes += this.outlined()
          ? 'text-red-500 border-red-500 disabled:hover:bg-transparent disabled:hover:text-red-500 hover:md:bg-red-500 hover:md:text-white'
          : 'text-white bg-red-500 disabled:hover:bg-red-500 disabled:hover:text-white hover:md:bg-red-600';
        break;
      case ButtonColor.Green:
        classes += this.outlined()
          ? 'text-green-500 border-green-500 disabled:hover:bg-transparent disabled:hover:text-green-500 hover:md:bg-green-500 hover:md:text-white'
          : 'text-white bg-green-500 disabled:hover:bg-green-500 disabled:hover:text-white hover:md:bg-green-600';
        break;
      default:
        break;
    }

    if (this.disabled() || this.isLoading()) {
      classes += ' opacity-50 cursor-not-allowed';
    }

    return classes;
  });

  isDisabled = computed(() => this.disabled() || this.isLoading());

  buttonClick(event: MouseEvent | TouchEvent): void {
    if (this.isDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.gtagLabel()) {
      this.gtagService.trackEvent('Button', 'Click', this.gtagLabel());
    }
  }
}
